import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { correctAhaModuleId, correctAhaProductNumber, getAhaProductIdentifiers } from "../../api/apiClient";

const message = (error) => error?.response?.data?.message || "The AHA identifier could not be saved.";

export default function AhaIdentifiersDialog({ open, onClose, productKey, onChanged }) {
  const [identifiers, setIdentifiers] = useState(null);
  const [selected, setSelected] = useState(null);
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !productKey) return;
    setIdentifiers(null);
    setSelected(null);
    setError("");
    getAhaProductIdentifiers(productKey).then(setIdentifiers).catch((e) => setError(message(e)));
  }, [open, productKey]);

  const choose = (kind, module = null) => {
    const current = module ? module.moduleId : identifiers.productNumber;
    setSelected({ kind, moduleKey: module?.key, label: module?.name || "Product number", current });
    setValue(current || "");
    setReason("");
    setError("");
  };

  const save = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError("");
    try {
      const updated = selected.kind === "product"
        ? await correctAhaProductNumber(productKey, value.trim(), selected.current, reason.trim())
        : await correctAhaModuleId(productKey, selected.moduleKey, value.trim(), selected.current, reason.trim());
      setIdentifiers(updated);
      setSelected(null);
      if (onChanged) onChanged();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="aha-identifiers-title">
      <DialogTitle id="aha-identifiers-title">AHA catalog identifiers</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          These identifiers are shared by every training center in this environment. Changes affect future AHA card requests;
          a module ID can also be used by other products. Cards already requested keep the numbers they used.
        </Alert>
        {identifiers && <Typography variant="body2" sx={{ mb: 2 }}>Environment: {identifiers.environment}</Typography>}
        {identifiers && !selected && (
          <Box sx={{ display: "grid", gap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
              <Typography>Product number: <b>{identifiers.productNumber || "Missing"}</b></Typography>
              <Button onClick={() => choose("product")}>Update</Button>
            </Box>
            {identifiers.modules.map((module) => (
              <Box key={module.key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Typography>{module.name}: <b>{module.moduleId || "Missing"}</b></Typography>
                <Button onClick={() => choose("module", module)} disabled={!module.moduleId}>Update</Button>
              </Box>
            ))}
          </Box>
        )}
        {selected && (
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <Typography>Update {selected.label} (current: {selected.current})</Typography>
            <TextField label="New AHA ID" value={value} onChange={(e) => setValue(e.target.value)} required
              helperText={selected.kind === "product" ? "Example: 25-3001" : "1 to 10 digits"} />
            <TextField label="Reason for change" value={reason} onChange={(e) => setReason(e.target.value)} required multiline minRows={2}
              helperText="Include the AHA notice or ticket reference (10 to 500 characters)." />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={() => setSelected(null)} disabled={busy}>Back</Button>
              <Button variant="contained" onClick={save} disabled={busy || value.trim() === selected.current || reason.trim().length < 10}>
                Save identifier
              </Button>
            </Box>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Close</Button></DialogActions>
    </Dialog>
  );
}
